/**
 * Product Delivery Service
 * Adapts UnifiedDeliveryCalculationService for Product page usage
 * Handles display formatting and user-friendly messages
 */

import {
  UnifiedDeliveryCalculationService,
  DeliveryInput,
  DeliveryMethodAvailability,
} from "./UnifiedDeliveryCalculationService";
import { getUnifiedDeliveryData } from "../lib/unifiedDeliveryService";

export interface ProductDeliveryDisplayOptions {
  express: {
    available: boolean;
    displayText: string;
    countdown?: string | null;
    deliveryDate?: Date;
    reason?: string;
    nextAvailableMessage?: string;
  };
  rapido: {
    available: boolean;
    displayText: string;
    countdown?: string | null;
    availableDates: Date[];
    reason?: string;
  };
  standard: {
    available: boolean;
    displayText: string;
    countdown?: string | null;
    earliestDate?: Date;
    reason?: string;
  };
}

export class ProductDeliveryService {
  /**
   * Calculate delivery options for Product page display
   */
  static async calculateProductDeliveryOptions(
    product: any,
    destinationLeadTimeZoneId: string,
    destinationFeeZoneId: string
  ): Promise<ProductDeliveryDisplayOptions> {
    try {
      const logistics = product.logistics;
      const partner = product.partner;

      if (!logistics || !partner?.daysOpen) {
        return this.getUnavailableResult("Información de logística incompleta");
      }

      // Get route availability from backend
      const deliveryData = await getUnifiedDeliveryData({
        originFeeZoneId: partner.deliveryFeeZone?.documentId,
        originLeadTimeZoneId: partner.deliveryLeadTimeZone?.documentId,
        destinationFeeZoneId,
        destinationLeadTimeZoneId,
      });

      if (!deliveryData?.deliveryMethods) {
        return this.getUnavailableResult(
          "Métodos de entrega no disponibles para esta ruta"
        );
      }

      // Prepare input for unified service
      const input: DeliveryInput = {
        logistics: {
          expressDelivery: logistics.expressDelivery === true,
          limitTime: logistics.limitTime || "14:00",
          daysToDeliver:
            logistics.minDaysToDeliver || logistics.daysToDeliver || 1,
          minDaysToDeliver: logistics.minDaysToDeliver,
          weight: logistics.weight,
        },
        partnerSchedule: partner.daysOpen,
        zones: {
          originFeeZoneId: partner.deliveryFeeZone?.documentId,
          originLeadTimeZoneId: partner.deliveryLeadTimeZone?.documentId,
          destinationFeeZoneId,
          destinationLeadTimeZoneId,
        },
        opsLeadTime: deliveryData.opsLeadTime.opsLeadTime || 1,
      };

      const routeAvailability: DeliveryMethodAvailability = {
        express: !!deliveryData.deliveryMethods.express,
        rapido: !!deliveryData.deliveryMethods.rapido,
        standard: !!deliveryData.deliveryMethods.standard,
      };

      // Calculate delivery options using new unified service
      const result =
        await UnifiedDeliveryCalculationService.calculateDeliveryOptions(
          input,
          routeAvailability
        );

      // Format for Product page display
      return {
        express: {
          available: result.express.available,
          displayText: result.express.available
            ? "Recíbelo en menos de 3 horas"
            : result.express.reason || "No disponible",
          countdown: result.express.countdown || null,
          deliveryDate: result.express.deliveryDate,
          reason: result.express.reason,
          nextAvailableMessage: result.express.nextAvailableMessage,
        },
        rapido: {
          available: result.rapido.available,
          displayText:
            result.rapido.available && result.rapido.availableDates.length > 0
              ? `A partir del ${this.formatDateForDisplay(
                  result.rapido.availableDates[0]
                )}`
              : result.rapido.reason || "No disponible",
          countdown: result.rapido.countdown || null,
          availableDates: result.rapido.availableDates,
          reason: result.rapido.reason,
        },
        standard: {
          available: result.standard.available,
          displayText:
            result.standard.available && result.standard.earliestDate
              ? `A partir del ${this.formatDateForDisplay(
                  result.standard.earliestDate
                )}`
              : result.standard.reason || "No disponible",
          countdown: result.standard.countdown || null,
          earliestDate: result.standard.earliestDate,
          reason: result.standard.reason,
        },
      };
    } catch (error) {
      console.error("Error calculating product delivery options:", error);
      return this.getUnavailableResult("Error al calcular opciones de entrega");
    }
  }

  /**
   * Format date for display
   */
  private static formatDateForDisplay(date: Date): string {
    return UnifiedDeliveryCalculationService.formatDeliveryDateForDisplay(date);
  }

  /**
   * Get result when delivery options are unavailable
   */
  private static getUnavailableResult(
    reason: string
  ): ProductDeliveryDisplayOptions {
    return {
      express: {
        available: false,
        displayText: reason,
        countdown: null,
        nextAvailableMessage: undefined,
      },
      rapido: {
        available: false,
        displayText: reason,
        countdown: null,
        availableDates: [],
      },
      standard: { available: false, displayText: reason, countdown: null },
    };
  }
}
